import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import type { DatesSetArg, EventClickArg, EventContentArg } from "@fullcalendar/core";
import type {
  CalendarEventResponse,
  CallCalendarItem,
  MyTournamentGame,
} from "../../../../types/api";
import { getEvent, getEvents, type CalendarScope } from "../../../../services/calendar";
import { getCallCalendarItems } from "../../../../services/calls";
import { formatEventTime, formatEventClockTime } from "../../../../utils/calendarTime";
import { formatCallClockTime } from "../calls/callTime";
import { useAuth } from "../../../../hooks/useAuth";
import {
  getStoredScheduleScope,
  saveScheduleScope,
} from "../../../../utils/scheduleScopeStorage";
import { EventEditorPanel } from "./EventEditorPanel";
import { EventViewPanel } from "./EventViewPanel";
import { allEventCoaches, isCoveringEvent } from "./gameCoaches";
import { ItineraryPanel } from "./ItineraryPanel";
import { ItineraryCatalogPanel } from "./ItineraryCatalogPanel";
import { BulkEventImportModal } from "./BulkEventImportModal";
import { ScheduleUpdatesList } from "./ScheduleUpdatesList";
import { SharedEventsBanner } from "./SharedEventsBanner";
import { Button } from "../../../../components/Button";
import { SearchIcon } from "../../../../assets/SearchIcon";
import { buildCalendarEvents } from "./scheduleCalendarEvents";
import { eventNeedsSetup, itinerarySetupUrl } from "./itinerarySetup";
import { eventShowsUnassignedTag, scheduleTourTargets } from "./scheduleTourTargets";
import { tourAnchor, type TourAnchorId } from "../../../tours/tourAnchors";
import { useTour } from "../../../tours/useTour";
import { PrintScheduleLink } from "./print/PrintScheduleLink";
import { printWindowFromView } from "./print/schedulePrint";
import "./ScheduleSection.css";

/**
 * Tags a calendar item as a tour anchor when it is the one `scheduleTourTargets` picked. FullCalendar
 * renders the events, so "first row" is decided by id rather than by list index.
 */
const eventAnchor = (eventId: string, targetId: string | null, id: TourAnchorId) =>
  eventId === targetId ? tourAnchor(id) : {};

export const ScheduleSection = () => {
  const navigate = useNavigate();
  const { user, effectiveOrganizationId } = useAuth();
  const currentUserId = user?.profile?.id;

  const [events, setEvents] = useState<CalendarEventResponse[]>([]);
  // Scheduled recruiting calls for the same window. A call with no time is not here — it stays
  // on the call list until someone gives it one, which the API enforces.
  const [calls, setCalls] = useState<CallCalendarItem[]>([]);
  const [error, setError] = useState("");
  // False until the first range has come back (either way), so a tour never measures an empty grid
  // that is only empty because nothing has loaded yet.
  const [loaded, setLoaded] = useState(false);
  // Everyone defaults to their personal ("mine") schedule. Hydrate the persisted
  // choice synchronously from the active org id so the very first fetch (fired by
  // FullCalendar's datesSet on mount) already uses the correct scope — no
  // fetch-then-correct race. getStoredScheduleScope falls back to DEFAULT_SCOPE
  // when there is no org or no stored value.
  const [scope, setScope] = useState<CalendarScope>(() =>
    getStoredScheduleScope(effectiveOrganizationId),
  );
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEventResponse | null>(null);
  // Viewing (read-only) is separate from editing: a click opens the view panel; Edit opens the editor.
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingEvent, setViewingEvent] = useState<CalendarEventResponse | null>(null);
  // The itinerary panel: a game-item click opens it focused on that game; the
  // event view's "Open itinerary" button opens it unfocused.
  const [itineraryOpen, setItineraryOpen] = useState(false);
  const [itineraryEvent, setItineraryEvent] = useState<CalendarEventResponse | null>(null);
  const [itineraryFocusGameId, setItineraryFocusGameId] = useState<string | undefined>(undefined);
  const [editFocusLocation, setEditFocusLocation] = useState(false);
  const [createDate, setCreateDate] = useState<string | undefined>(undefined);
  // The single "+ New" entry point opens a small menu: quick event vs. build itinerary.
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const newMenuRef = useRef<HTMLDivElement>(null);

  // Collapsible "Updates" panel: the schedule-update notification feed. Kept
  // collapsed by default so it stays non-intrusive; the list itself owns its
  // fetch and unread count.
  const [updatesOpen, setUpdatesOpen] = useState(false);

  // The shared itinerary catalog: browse published tournaments by date window, add one to this
  // org's calendar, or ask RosterPilot to build the one that isn't there.
  const [catalogOpen, setCatalogOpen] = useState(false);
  // Stable identity: useOverlayDialog depends on this to register its keydown listener, so a fresh
  // arrow per render would tear the listener down and re-add it on every parent re-render.
  const closeCatalog = useCallback(() => setCatalogOpen(false), []);

  // Bulk add: upload a schedule document, review what the model read off it, and drop the events
  // onto the calendar individually. Conditionally mounted, so each open starts from the picker.
  const [bulkOpen, setBulkOpen] = useState(false);
  const closeBulk = useCallback(() => setBulkOpen(false), []);

  // Ids of distributed copies the viewer still needs to set up. Lets the Updates
  // feed offer a "Set up your itinerary" CTA on the matching notification without
  // a new API — we already hold the events (which carry needsSetup) here.
  const needsSetupEventIds = useMemo(
    () => new Set(events.filter(eventNeedsSetup).map((e) => e.id)),
    [events],
  );

  // The month on screen, for the printout. Not the fetch range: that is the whole grid, with the
  // neighbouring months' leading and trailing days, which a coach would not expect on paper.
  const [printWindow, setPrintWindow] = useState<{ from: string; to: string } | null>(null);

  // Remember the visible window + scope so we can refetch after a create/edit/delete/toggle.
  const rangeRef = useRef<{ from: string; to: string } | null>(null);
  const scopeRef = useRef<CalendarScope>(scope);

  // Monotonic token guarding against out-of-order responses: paging months quickly (or a scope
  // toggle mid-flight) must not let a slow earlier fetch overwrite fresher state. Load-bearing
  // now that one visible range means two requests, which can interleave with each other.
  const fetchTokenRef = useRef(0);

  const fetchRange = useCallback(async (fromIso: string, toIso: string) => {
    const token = ++fetchTokenRef.current;
    const scope = scopeRef.current;
    // Settled rather than all: the two reads are independent surfaces of one grid, and a calls
    // outage must not blank the events the coach came here for (or vice versa).
    const [eventsResult, callsResult] = await Promise.allSettled([
      getEvents(fromIso, toIso, scope),
      getCallCalendarItems(fromIso, toIso, scope),
    ]);
    if (token !== fetchTokenRef.current) return;
    setLoaded(true);

    if (eventsResult.status === "fulfilled") setEvents(eventsResult.value);
    if (callsResult.status === "fulfilled") setCalls(callsResult.value);

    // The events failure wins the banner: it is the bulk of the calendar. A calls-only failure
    // gets its own copy so the coach knows what is missing rather than assuming they have no
    // calls booked.
    if (eventsResult.status === "rejected") {
      setError("Couldn't load the calendar. Try refreshing.");
    } else if (callsResult.status === "rejected") {
      setError("Couldn't load your scheduled calls. The rest of the calendar is up to date.");
    } else {
      setError("");
    }
  }, []);

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      const from = arg.start.toISOString();
      const to = arg.end.toISOString();
      rangeRef.current = { from, to };
      setPrintWindow(printWindowFromView(arg.view.currentStart, arg.view.currentEnd));
      void fetchRange(from, to);
    },
    [fetchRange],
  );

  const refetch = useCallback(() => {
    if (rangeRef.current) {
      void fetchRange(rangeRef.current.from, rangeRef.current.to);
    }
  }, [fetchRange]);

  // The initial scope is hydrated synchronously in useState above. This effect
  // only handles a later org change (a system-admin switching orgs, or the org
  // id resolving after mount): re-hydrate that org's remembered scope so the
  // toggle never leaks another tenant's choice, and refetch only when it
  // actually differs from what we've been showing.
  useEffect(() => {
    if (!effectiveOrganizationId) return;
    const stored = getStoredScheduleScope(effectiveOrganizationId);
    if (scopeRef.current === stored) return;
    setScope(stored);
    scopeRef.current = stored;
    refetch();
  }, [effectiveOrganizationId, refetch]);

  const changeScope = (next: CalendarScope) => {
    if (next === scope) return;
    setScope(next);
    scopeRef.current = next;
    saveScheduleScope(effectiveOrganizationId, next);
    refetch();
  };

  // Dismiss the "+ New" menu on outside click / Escape.
  useEffect(() => {
    if (!newMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (newMenuRef.current && !newMenuRef.current.contains(e.target as Node)) {
        setNewMenuOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setNewMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [newMenuOpen]);

  useTour("schedule", { ready: loaded, hasData: events.length > 0 });

  const calendarEvents = buildCalendarEvents(events, scope, currentUserId, calls);
  const tourTargets = scheduleTourTargets(calendarEvents);

  const renderEventContent = (arg: EventContentArg) => {
    const raw = arg.event.extendedProps.raw as CalendarEventResponse | undefined;
    // A scheduled recruiting call: the player's name and the time, plus who is making it.
    const call = arg.event.extendedProps.call as CallCalendarItem | undefined;
    if (call) {
      return (
        <span
          className="schedule-event"
          title={[
            `Call with ${call.prospectName}`,
            call.assignedCoachName,
            call.purpose,
          ]
            .filter(Boolean)
            .join(" — ")}
          data-testid={`schedule-call-${call.id}`}
        >
          <span className="schedule-event__main">
            <span className="schedule-event__time">
              {formatCallClockTime(call.scheduledAt)}
            </span>
            📞 {call.prospectName}
          </span>
        </span>
      );
    }
    // "My events" per-game item: show the game's own time + matchup, not the tournament title.
    const game = arg.event.extendedProps.game as MyTournamentGame | undefined;
    if (game) {
      return (
        <span
          className="schedule-event"
          title={`${raw?.title ?? "Event"} — your game`}
          {...eventAnchor(arg.event.id, tourTargets.open, "schedule.row.open")}
        >
          <span className="schedule-event__main">
            <span className="schedule-event__time">
              {formatEventClockTime({ startsAtUtc: game.startsAtUtc, timeZone: game.timeZone })}
            </span>
            {game.homeTeamNameRaw} vs {game.awayTeamNameRaw}
          </span>
        </span>
      );
    }
    if (!raw) return <span>{arg.event.title}</span>;
    // Game-level coaches count too, so a tournament covered only game by game isn't "Unassigned".
    const assignees = allEventCoaches(raw);
    const shown = assignees.slice(0, 3);
    const extra = assignees.length - shown.length;
    // Only tournaments carry the org-wide "Unassigned" tag when nobody's on them; recruit visits
    // and one-offs just show their assignees (or nothing) since they're personal by nature.
    const showUnassignedTag = eventShowsUnassignedTag(raw);
    const assigneeTitle = assignees.length
      ? `Assigned: ${assignees.map((a) => a.displayName).join(", ")}`
      : showUnassignedTag
        ? "Org-wide, nobody assigned yet"
        : undefined;
    // Distributed copies the viewer hasn't set up yet get a small "Needs setup"
    // badge so recipients can spot them at a glance in the list.
    const needsSetup = eventNeedsSetup(raw);
    return (
      <span
        className="schedule-event"
        title={assigneeTitle}
        {...eventAnchor(arg.event.id, tourTargets.open, "schedule.row.open")}
      >
        <span className="schedule-event__main">
          {!raw.isAllDay && <span className="schedule-event__time">{formatEventTime(raw)}</span>}
          {raw.title}
        </span>
        {/* Tags take priority over the title: they never shrink, and the title
            truncates before they get pushed off the calendar cell. */}
        <span className="schedule-event__tags">
          {needsSetup && (
            <span
              className="schedule-needs-setup"
              title="A shared schedule that still needs setup"
              data-testid={`schedule-needs-setup-${raw.id}`}
              {...eventAnchor(arg.event.id, tourTargets.needsSetup, "schedule.row.needs-setup")}
            >
              Needs setup
            </span>
          )}
          {assignees.length > 0 ? (
            <span className="schedule-avs">
              {shown.map((a) => (
                <span
                  key={a.userId}
                  className={`schedule-avs__mini${a.userId === currentUserId ? " schedule-avs__mini--you" : ""}`}
                  title={a.displayName}
                >
                  {a.initials}
                </span>
              ))}
              {extra > 0 && (
                <span
                  className="schedule-avs__mini"
                  title={assignees.slice(shown.length).map((a) => a.displayName).join(", ")}
                >
                  +{extra}
                </span>
              )}
            </span>
          ) : showUnassignedTag ? (
            <span
              className="schedule-orgwide"
              title="Org-wide, nobody assigned yet"
              {...eventAnchor(arg.event.id, tourTargets.unassigned, "schedule.row.unassigned")}
            >
              Unassigned
            </span>
          ) : null}
        </span>
      </span>
    );
  };

  const eventClassNames = (arg: {
    event: {
      extendedProps: { raw?: CalendarEventResponse; call?: CallCalendarItem };
    };
  }) => {
    const call = arg.event.extendedProps.call;
    if (call) {
      // A call is "mine" by assignment, the same accent an event gets for an assignee match.
      return currentUserId && call.assignedCoachId === currentUserId ? ["ev-mine"] : [];
    }
    const raw = arg.event.extendedProps.raw;
    if (raw && isCoveringEvent(raw, currentUserId)) {
      return ["ev-mine"];
    }
    return [];
  };

  const openCreate = () => {
    setEditingEvent(null);
    setCreateDate(new Date().toISOString().slice(0, 10));
    setEditorOpen(true);
  };

  const handleEventClick = (arg: EventClickArg) => {
    const raw = arg.event.extendedProps.raw as CalendarEventResponse | undefined;
    const game = arg.event.extendedProps.game as MyTournamentGame | undefined;
    const call = arg.event.extendedProps.call as CallCalendarItem | undefined;
    if (call) {
      // Clicking a call takes you to the call itself. The call list owns every action on a call
      // (notes, complete, reschedule, dial), so deep-linking there beats a second, thinner copy
      // of that panel on the schedule.
      navigate(`/calls?callId=${encodeURIComponent(call.id)}`);
      return;
    }
    if (!raw) return;
    if (game) {
      // A "My events" per-game item opens the itinerary focused on that game,
      // not the whole-tournament view.
      setItineraryEvent(raw);
      setItineraryFocusGameId(game.gameId);
      setItineraryOpen(true);
      return;
    }
    // Clicking an event bar VIEWS it (read-only + weather); Edit is a step from there.
    setViewingEvent(raw);
    setViewOpen(true);
  };

  // From the event view's "Open itinerary": open the itinerary unfocused for the
  // same tournament (no specific game to land on).
  const openItineraryFromView = () => {
    if (!viewingEvent) return;
    setViewOpen(false);
    setItineraryEvent(viewingEvent);
    setItineraryFocusGameId(undefined);
    setItineraryOpen(true);
  };

  // From the itinerary's "View full event": swap to the read-only event view.
  const viewFullEventFromItinerary = () => {
    if (!itineraryEvent) return;
    setItineraryOpen(false);
    setViewingEvent(itineraryEvent);
    setViewOpen(true);
  };

  // From the view panel: hand off to the editor for the same event. `focusLocation` comes from the
  // "Set location" prompt so the editor opens focused on the location field.
  const handleEditFromView = (focusLocation = false) => {
    setViewOpen(false);
    setEditingEvent(viewingEvent);
    setCreateDate(undefined);
    setEditFocusLocation(focusLocation);
    setEditorOpen(true);
  };

  /**
   * Open a tournament named on the shared-events banner. It is usually already in the loaded range
   * (the banner and the calendar are the same org's data), but a share for a month the coach is
   * not currently looking at is not, so fall back to fetching that one event rather than doing
   * nothing when the row is clicked.
   */
  const openSharedEvent = useCallback(
    (calendarEventId: string) => {
      const loaded = events.find((e) => e.id === calendarEventId);
      if (loaded) {
        setViewingEvent(loaded);
        setViewOpen(true);
        return;
      }
      getEvent(calendarEventId)
        .then((fetched) => {
          setViewingEvent(fetched);
          setViewOpen(true);
        })
        .catch(() => {
          setError("Couldn't open that tournament. Try refreshing.");
        });
    },
    [events],
  );

  const handleSaved = () => {
    setEditorOpen(false);
    refetch();
  };

  return (
    <div className="schedule-section">
      <div className="schedule-main">
        <div className="schedule-header">
          <div className="schedule-new" ref={newMenuRef}>
            {/*
                A design-system `Button`, not a hand-rolled one. The bespoke `.schedule-new__btn`
                carried its own padding (9px vs the primitive's 12.5px), weight and radius, so it
                rendered visibly shorter than the "Find an itinerary" button sitting right beside
                it. `Button` spreads its remaining props, so the menu's ARIA travels through.
            */}
            <Button
              type="button"
              variant="primary"
              aria-haspopup="menu"
              aria-expanded={newMenuOpen}
              onClick={() => setNewMenuOpen((v) => !v)}
              data-testid="schedule-new-cta"
              {...tourAnchor("schedule.new")}
            >
              + New
            </Button>
            {newMenuOpen && (
              <div className="schedule-new__menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="schedule-new__item"
                  onClick={() => {
                    setNewMenuOpen(false);
                    openCreate();
                  }}
                  data-testid="schedule-new-quick"
                >
                  <span className="schedule-new__item-title">Quick event</span>
                  <span className="schedule-new__item-sub">
                    A one-off event or a recruit visit
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="schedule-new__item"
                  onClick={() => {
                    setNewMenuOpen(false);
                    navigate("/schedule/build-itinerary");
                  }}
                  data-testid="build-itinerary-cta"
                >
                  <span className="schedule-new__item-title">Build itinerary</span>
                  <span className="schedule-new__item-sub">
                    Import a schedule and build a coach-assigned itinerary
                  </span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="schedule-new__item"
                  onClick={() => {
                    setNewMenuOpen(false);
                    setBulkOpen(true);
                  }}
                  data-testid="bulk-events-cta"
                >
                  <span className="schedule-new__item-title">Bulk add events</span>
                  <span className="schedule-new__item-sub">
                    Upload a schedule and we'll pull the events out of it
                  </span>
                </button>
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="secondary"
            className="schedule-find-itinerary"
            onClick={() => setCatalogOpen(true)}
            data-testid="find-itinerary-cta"
            {...tourAnchor("schedule.find-itinerary")}
          >
            {/* A real icon, not an emoji: an emoji renders in whatever the OS ships, at its own
                weight and colour, so it never matches the label beside it. This inherits
                currentColor and the button's own type scale. */}
            <SearchIcon className="schedule-find-itinerary__icon" />
            Find an itinerary
          </Button>
          {printWindow && (
            <PrintScheduleLink
              request={{ kind: "range", ...printWindow, scope }}
              data-testid="schedule-print"
              {...tourAnchor("schedule.print")}
            />
          )}
        </div>
        <div className="schedule-legend">
          <span className="schedule-legend__key">
            <span className="schedule-legend__dot" style={{ background: "#7c3aed" }} /> Event
          </span>
          <span className="schedule-legend__key">
            <span className="schedule-legend__dot" style={{ background: "#0891b2" }} /> Recruit visit
          </span>
          <span className="schedule-legend__key">
            <span className="schedule-legend__dot" style={{ background: "#d97706" }} /> One-off
          </span>
          <span className="schedule-legend__key">
            <span className="schedule-legend__dot" style={{ background: "#16a34a" }} /> Call
          </span>
          <span className="schedule-legend__key">
            <span className="schedule-orgwide schedule-orgwide--legend">Unassigned</span> Nobody
            assigned yet
          </span>
          <span
            className="schedule-scope"
            role="group"
            aria-label="Schedule scope"
            {...tourAnchor("schedule.scope")}
          >
            <button
              type="button"
              className={`schedule-scope__btn${scope === "all" ? " schedule-scope__btn--on" : ""}`}
              aria-pressed={scope === "all"}
              onClick={() => changeScope("all")}
            >
              All events
            </button>
            <button
              type="button"
              className={`schedule-scope__btn${scope === "mine" ? " schedule-scope__btn--on" : ""}`}
              aria-pressed={scope === "mine"}
              onClick={() => changeScope("mine")}
            >
              My events
            </button>
          </span>
        </div>

        {error && <div className="schedule-error">{error}</div>}

        {/* REQ-11: page-level and visible on arrival. The collapsed Updates panel below is still
            there and still works, but a coach who has had three tournaments shared with them has
            to be told so WITHOUT expanding anything, which is exactly what a collapsed panel
            cannot do. */}
        <SharedEventsBanner onOpenEvent={openSharedEvent} />

        <div className="schedule-updates-panel" {...tourAnchor("schedule.updates")}>
          <button
            type="button"
            className="schedule-updates-panel__toggle"
            aria-expanded={updatesOpen}
            aria-controls="schedule-updates-panel-body"
            onClick={() => setUpdatesOpen((v) => !v)}
            data-testid="schedule-updates-toggle"
          >
            <span className="schedule-updates-panel__toggle-caret" aria-hidden="true">
              {updatesOpen ? "▾" : "▸"}
            </span>
            Updates
          </button>
          {updatesOpen && (
            <div id="schedule-updates-panel-body">
              <ScheduleUpdatesList
                needsSetupEventIds={needsSetupEventIds}
                onSetUp={(eventId) => navigate(itinerarySetupUrl(eventId))}
              />
            </div>
          )}
        </div>

        <div className="schedule-calendar" {...tourAnchor("schedule.calendar")}>
          <FullCalendar
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            timeZone="UTC"
            height="100%"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "",
            }}
            buttonText={{ month: "Month", today: "Today" }}
            events={calendarEvents}
            eventContent={renderEventContent}
            eventClassNames={eventClassNames}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            dayMaxEvents={3}
            firstDay={0}
          />
        </div>
      </div>

      {viewOpen && viewingEvent && (
        <div
          className="event-editor-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setViewOpen(false);
          }}
        >
          <EventViewPanel
            event={viewingEvent}
            onEdit={handleEditFromView}
            onOpenItinerary={openItineraryFromView}
            onClose={() => setViewOpen(false)}
            onChanged={refetch}
          />
        </div>
      )}

      {itineraryOpen && itineraryEvent && (
        <div
          className="event-editor-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setItineraryOpen(false);
          }}
        >
          <ItineraryPanel
            event={itineraryEvent}
            focusGameId={itineraryFocusGameId}
            onViewFullEvent={viewFullEventFromItinerary}
            onClose={() => setItineraryOpen(false)}
          />
        </div>
      )}

      {catalogOpen && (
        <div
          className="event-editor-overlay"
          // Presentational: clicking the dim backdrop is a convenience duplicate of the panel's own
          // close button, which is the real, focusable, keyboard-reachable affordance. The panel also
          // closes on Escape. Marking the role keeps this out of the tab order and out of the
          // accessibility tree rather than announcing a div nobody can operate.
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setCatalogOpen(false);
          }}
        >
          <ItineraryCatalogPanel onClose={closeCatalog} onAdded={refetch} />
        </div>
      )}

      {bulkOpen && (
        <BulkEventImportModal onClose={closeBulk} onCreated={refetch} />
      )}

      {editorOpen && (
        <div
          className="event-editor-overlay"
          onMouseDown={(e) => {
            // Close only when the dim backdrop itself is clicked, not the panel.
            if (e.target === e.currentTarget) setEditorOpen(false);
          }}
        >
          <EventEditorPanel
            event={editingEvent}
            defaultDate={createDate}
            focusLocation={editFocusLocation}
            onClose={() => setEditorOpen(false)}
            onSaved={handleSaved}
            onDeleted={handleSaved}
          />
        </div>
      )}
    </div>
  );
};
